import { AfterViewInit, Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../../../../state/app.state';
import { CommonService } from '../../../../../../../../shared/services/common.service';
import { ProjectsService } from '../../../../../../core/projects.service';
import { AuthService } from '../../../../../../../../shared/services';
import { MatDialog } from '@angular/material/dialog';
import { takeUntil } from 'rxjs/operators';
import { allBidItems, basisOfPayOptions } from '../../../../../../core/projects.selectors';

@Component({
    selector: 'app-change-orders-request-line-item',
    templateUrl: './change-orders-request-line-item.component.html',
    styleUrls: ['./change-orders-request-line-item.component.scss'],
    standalone: false
})
export class ChangeOrdersRequestLineItemComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() lineKey: any;
  @Input() formGroup: any;
  alreadyExists = false;
  bidItemsList = [];
  filteredBidItemsList = [];
  focusedIn = false;
  searchText: string;
  errorMessages: any = {};
  errorMessageMap = {
    searchText: { required: 'this field is required' },
    itemNo: { required: 'this field is required' },
    item: { required: 'this field is required' },
    uom: { required: 'this field is required' },
    desc: { required: 'this field is required' },
    basisOfPay: { required: 'this field is required' },
    quantity: { required: 'this field is required' },
    changeInQuantity: { required: 'this field is required' },
    unitPrice: { required: 'this field is required' },
    totalAmount: { required: 'this field is required' },
    additionalWorkingDays: { required: 'this field is required' },
    locationOfChange: { required: 'this field is required' },
  };
  form = new FormGroup<any>({});
  totalAmount = 0;
  totalNewWorkingDays = 0;
  basisOfPayOptions = [];
  displayItemNo = false;

  isFormDirty = false;
  editable = true;

  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private commonService: CommonService,
    private projectsService: ProjectsService,
    public authService: AuthService,
    public dialog: MatDialog,
  ) {
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    const target = event.target as HTMLElement;

    // if click is NOT inside the search input or results list → close
    if (!target.closest('.search-section') && !target.closest('.bid-items-list')) {
      this.focusedIn = false;
      this.filteredBidItemsList = this.bidItemsList;
    }
  }

  ngOnInit() {
    this.form = this.formGroup;

    this.form?.controls.unitPrice.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.recomputeTotalAmount();
      });

    this.form?.controls?.changeInQuantity.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.recomputeTotalAmount();
      });

    this.form?.controls?.totalAmount.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data !== null && data !== undefined) {
          this.projectsService.totalAmount.next(data);
        }
      });

    this.form?.controls?.additionalWorkingDays.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data !== null && data !== undefined) {
          this.projectsService.totalAdditionalWorkingDays.next(data);
        }
      });

    this.projectsService.isAnyFormDirty
      .pipe(takeUntil(this.onDestroy))
      .subscribe((dirty) => {
        this.isFormDirty = dirty;
      });

    this.projectsService.checkForErrors
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.checkForErrors();
      });

    this.store.select(allBidItems)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((bidItems) => {
        this.bidItemsList = bidItems;
        this.filteredBidItemsList = bidItems;
      });

    this.store.select(basisOfPayOptions)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.basisOfPayOptions = data;
      });

    this.commonService.focusedIn
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data) {
          this.focusedIn = data;

          if (this.searchText) {
            this.filteredBidItemsList = this.bidItemsList.filter(item => {
              const name = item.item;
              const description = item.description;

              if (name?.toLowerCase().includes(this.searchText.toLowerCase())) {
                return true;
              }

              if (description.toLowerCase().includes(this.searchText.toLowerCase())) {
                return true;
              }
            });
          } else {
            this.filteredBidItemsList = this.bidItemsList;
          }
        } else {
          this.filteredBidItemsList = [];
          this.focusedIn = false;
        }
      });

    this.form?.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe((value) => {
        const payload = {
          lineKey: this.lineKey,
          value,
        };
        this.projectsService.addValueToParentForm.next(payload);

        this.projectsService.isAnyFormDirty.next(true);
      });
  }

  ngAfterViewInit() {
    this.projectsService.isAnyFormDirty.next(false);
  }

  /**
   * Amount change for the line. Lines with a repricing snapshot (loaded from
   * the API as originalUnitPrice/quantity) account for the price change on
   * the original quantity; others are simply price * delta quantity.
   */
  private recomputeTotalAmount() {
    const newPrice = Number(this.form?.controls?.unitPrice?.value) || 0;
    const deltaQty = Number(this.form?.controls?.changeInQuantity?.value) || 0;
    const existingQty = Number(this.form?.controls?.quantity?.value) || 0;
    const oldPrice = this.form?.controls?.originalUnitPrice?.value;

    const total = oldPrice != null
      ? newPrice * (existingQty + deltaQty) - (Number(oldPrice) || 0) * existingQty
      : newPrice * deltaQty;

    this.form?.controls['totalAmount'].setValue(+total, { emitEvent: true });
  }

  selectBidItem(bidItem: any) {
    this.form?.patchValue({
      item: `${bidItem?.item} ${bidItem?.description}`,
      itemNo: bidItem?.item,
      desc: bidItem?.description,
      unitPrice: bidItem?.unit_price,
      quantity: bidItem?.quantity,
      originalUnitPrice: bidItem?.unit_price ?? null,
      uom: bidItem?.uom,
    });
    this.focusedIn = false;
  }

  addNewBidItem() {
    this.displayItemNo = true;
    this.form?.removeControl('searchText');
    this.form?.controls['itemNo'].enable();
    this.focusedIn = false;
  }

  checkForErrors(currentField?: string) {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField)),
    };
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
